import { Component, OnInit, Input, Output, EventEmitter, ViewChild, TemplateRef } from "@angular/core";
import { ComAccountsGridLayoutService } from "@irb/irb-editors/editor-portfolio/com-accounts/com-accounts.grid-layout.service";
import { CalltoactionService } from "@irb/irb-common/components/calltoaction/calltoaction.service";
import { AppService } from "@irb/app.service";
import { IrbEditorsEvents, IrbCreatePortfolioEvents } from "@irb-common/events";
import { IrbSettingsService } from "@irb/irb-settings/irb-settings.service";
import { IAccount } from '@irb/irb-clients/irb-clients.model';
import { EditorsComponent } from '@irb/irb-editors/irb-editors.com';
import { AnalyticsService } from '@irb/irb-common/analytics/analytics.service';
import { IGlobalSettings } from "@irb/irb-settings/irb-settings.model";
import { UtilityService } from "@irb-common/services/utility.service";
import { CurrentContextService, ContextType, IContext } from '@charlesschwab/as-ui-core';
import { IUserDetails } from "@irb/app.model";

@Component({ standalone: false,
  selector: "irb-com-accounts",
  templateUrl: "./com-accounts.component.html",
  styleUrls: ["./com-accounts.component.scss", "../../editor-sidebar/editor-sidebar.component.scss"]
})
export class ComAccountsComponent implements EditorsComponent<any>, OnInit {
  _data: any;
  @Input() globalSettings: IGlobalSettings;
  @Output() change = new EventEmitter<boolean>();
  rowCount: number;
  grid: any;
  gridOptions: any;
  isUiTest = false;
  isUnAuthorized: boolean = false;
  isDistHoldCashEnabled: boolean;
  frameworkComponents: any;
  ENABLE_FLYOUT: boolean = true;
  redirectURL: string;
  userDetails: IUserDetails;
  row: any;
  @ViewChild('accountNumberOverlay') accountNumberOverlay: TemplateRef<any>;
  sacMenuItems = [
    { sanc: 'A_BAL', label: 'Balances' },
    { sanc: 'A_POS', label: 'Positions' },
    { sanc: 'A_TRAN', label: 'Transactions' },
    { sanc: 'A_COST', label: 'Cost Basis' },
    { sanc: 'A_RMD', label: 'RMD' }
  ];

  constructor(
    private gls: ComAccountsGridLayoutService,
    private ctas: CalltoactionService,
    private as: AppService,
    public iss: IrbSettingsService, 
    private ats: AnalyticsService,
    private util: UtilityService,
    private cxs: CurrentContextService
  ) {
    this.userDetails = this.as.getItem("IRB_APP_CONFIG") as IUserDetails;
    this.ENABLE_FLYOUT = this.userDetails?.featureFlags?.ENABLE_FLYOUT;
    this.redirectURL = this.userDetails?.veoRedirectUrl;
    this.isDistHoldCashEnabled = this.userDetails?.featureFlags?.isDistHoldCashEnabled || false;
  }

  ngOnInit() { 
    this.setPortfolioAuthorization();
    this.gridOptions = {
      columnDefs: this.gls.getColumnDefs(this),      
      floatingFilter: false,
      overlayNoRowsTemplate: "No assigned accounts.",
      context: { componentParent: this },
      isExternalFilterPresent: this.isExternalFilterPresent.bind(this),
      doesExternalFilterPass: this.doesExternalFilterPass.bind(this)
    };   
    
    this.frameworkComponents = this.gls.getFrameworkComponents();
  }

  onGridReady(params: any) {
    this.grid = params;
    this.grid.api.onFilterChanged();
      if(this.globalSettings?.isSMAallowed === true) {
        this.grid.api.setColumnsVisible(['smaSymbolName'], true);
        this.grid.api.setColumnsVisible(['fundingAccountNumber'], true);
      }
      
      if(this.globalSettings?.isSMAallowed === false) {
        this.grid.api.setColumnsVisible(['smaSymbolName'], false);
        this.grid.api.setColumnsVisible(['fundingAccountNumber'], false);
      }

    this.load();
  }

  load() {
    this.grid.api.setGridOption('rowData', this.data.accountsSet);
    this.rowCount = this.getRowCount();
  }

  deleteAll() {
    for (let i = 0; i < this.data.accountsSet.length; i++) {
      if(this.data.accountsSet[i].isNew) {
        this.data.accountsSet.splice(i--,1);
      } else {
        this.data.accountsSet[i].isUIdeleted = true;
      }  
    }

    this.load();
    this.change.emit(true);
  }

  delete(cc: any) {
    for (let i = 0; i < this.data.accountsSet.length; i++) {
      if (this.data.accountsSet[i].accountId === cc.accountId) {
        if(this.data.accountsSet[i].isNew) {
          this.data.accountsSet.splice(i--,1);
        } else {
          this.data.accountsSet[i].isUIdeleted = true;
        }        
        break;
      }
    }

    this.load();
    this.change.emit(true);
  }

  cancelDelete() {
    this.ctas.remove();
  }

  getRowCount() {
    return (this.data&&this.data.accountsSet)?this.data.accountsSet.filter((x:any) => x.isUIdeleted === false).length:0;
  }

  isExternalFilterPresent() {
    return true;
  }

  doesExternalFilterPass(node: any) {
    return !node.data.isUIdeleted;
  }

  showEditAccount(data: any) {
    this.as.fireEvent(IrbEditorsEvents.SHOW_ACCOUNT_EDIT, data);
  }

  showAssignAccountsDialog() {
    this.ats.trackClickEvent("lc_editassignacct");

    this.as.fireEvent(IrbCreatePortfolioEvents.SHOW_PORTFOLIO_ACCOUNT_ASSIGNMENT, {
      portfolioName: this.data.portfolioName,
      portfolioId: this.data.portfolioId,
      isAltWorkflow: true,
      finishButtonCallback: ((portfolioId: number, portfolioAccounts: Array<IAccount>) => {
        this.as.fireEvent(IrbCreatePortfolioEvents.SHOW_PORTFOLIO_IMPORT_HOLDINGS, {
          portfolioId: portfolioId,
          portfolioAccounts: portfolioAccounts,
          isAltWorkflow: true,
          finishButtonLabel: "Done",
          finishButtonCallback: ((portfolioId: number, portfolioAccounts: Array<IAccount>) => {
            false && console.log(portfolioId, portfolioAccounts);
            this.as.fireEvent(IrbEditorsEvents.ASSIGN_ACCOUNTS, portfolioAccounts);
          })
        });
      })
    });
  }

  @Input()
  get data(): any {
    return this._data;
  }

  set data(d: any) {
    this._data = d;
  }

  setPortfolioAuthorization() {
    if (this.data && this.data.authStatus && this.data.authStatus === 'Unauthorized') {
        this.isUnAuthorized = true;
    } else {
        this.isUnAuthorized = false;
    }
  }

  getTemplate(params: any) {
    this.row = params.data;
    return this.accountNumberOverlay;
  }

  closeMenu() {
    this.ctas.remove();
  }

  openSACWithContext(sanc: string) {
    // If no row data, open SAC without context
    if (!this.row) {
      window.open(`${this.redirectURL}/?sanc=${sanc}`, '_blank');
      return;
    }

    const baseURL = this.util.getSacContextGatewayBaseURL();
    const firmId = this.userDetails?.firmId;

    this.cxs.init(baseURL, firmId);

    const currentContext: IContext = {
      contextId: this.row.accountNumber,
      contextType: ContextType.Account,
      name: this.row.accountName || this.row.accountNumber
    };

    this.cxs.save(currentContext)
      .then((ubid: string) => {
        const url = ubid
          ? `${this.redirectURL}/?sanc=${sanc}&ubid=${ubid}`
          : `${this.redirectURL}/?sanc=${sanc}`;
        window.open(url, '_blank');
      })
      .catch(() => {
        // Fallback: open SAC without UBID on error
        window.open(`${this.redirectURL}/?sanc=${sanc}`, '_blank');
      });
  }
}
